import { AddressResolver } from '@shared/execution/types';
import { FilterCondition, Flowstep } from '@shared/types/flowstep';
import { WorkbookApi } from '@shared/execution/workbook-api';
import { replaceTimestampValue } from "@shared/providers/common/address-resolving";

const substitutionRegex = /\{\{(.+?)}}/g;

export class HubspotAddressResolver implements AddressResolver {
  constructor(private workbookApi: WorkbookApi<any>) {}

  private convertFromExcel(value: any, type: string) {
    if (typeof value === 'number' && (type === 'date' || type === 'datetime')) {
      return excelDateToDate(value).toISOString();
    }
    return value;
  }

  async resolveConditionValues(flowstep: Flowstep) {
    const conditions = flowstep.providerOptions?.conditions as FilterCondition[];
    if (!conditions) {
      return;
    }

    for (let condition of conditions) {
      const { value } = condition;

      let regexResult;
      let result = value;

      while ((regexResult = substitutionRegex.exec(value)) !== null) {
        const expression = regexResult[0];
        const addressStr = regexResult[1];

        const range = await this.workbookApi.getRangeByAddress(addressStr);
        if (!range) {
          continue;
        }

        if (['IN', 'NOT_IN'].includes(condition.operator)) {
          const columnRange = this.workbookApi.getRangeColumn(range, 0);
          const rangeValues = await this.workbookApi.getRangeValues(columnRange);
          if (rangeValues) {
            result = rangeValues.map((row) => this.convertFromExcel(row[0], condition.type));
            break;
          }
        }

        const cellValue = await this.workbookApi.getRangeCellValue(range, 0, 0);
        result = result.replace(expression, this.convertFromExcel(cellValue, condition.type));
      }

      condition.value = result;
    }
  }

  private async replaceConstantsValues(flowstep: Flowstep) {
    const constants = flowstep.options.mapping?.constants;
    if (!constants) {
      return;
    }

    for (let constant of constants) {
      const { value } = constant;
      let regexResult;
      let result = value;

      while ((regexResult = substitutionRegex.exec(value)) !== null) {
        const expression = regexResult[0];
        const addressStr = regexResult[1];

        const range = await this.workbookApi.getRangeByAddress(addressStr);
        if (!range) {
          continue;
        }

        const cellValue = await this.workbookApi.getRangeCellValue(range, 0, 0);
        result = result.replace(expression, cellValue);
      }

      constant.value = result;
    }
  }

  async replaceAddressesByValues(flowstep: Flowstep) {
    const flowstepClone = structuredClone(flowstep);
    await replaceTimestampValue(flowstepClone, this.workbookApi);
    await this.replaceConstantsValues(flowstepClone);
    return flowstepClone;
  }
}

const DAY_MILLISECONDS = 86400000;
const MS_DAY_OFFSET = 25569;

const excelDateToDate = function (excelDate: number) {
  let ticks = (excelDate - MS_DAY_OFFSET) * DAY_MILLISECONDS;
  if (excelDate < 0) {
    const frac = (excelDate - Math.trunc(excelDate)) * DAY_MILLISECONDS;
    if (frac !== 0) {
      ticks -= frac * 2;
    }
  }
  return new Date(ticks);
};
